import React, { useState, useContext } from 'react';
import useAsyncEffect from 'use-async-effect';
import { Form } from 'react-bootstrap';
import Inputs from 'components/inputs/inputs';
import Flow from 'components/common/flow/flow';
import Panel from 'components/common/panel/panel';
import SiteContext from 'components/shared/siteContext/siteContext';
import Heading from 'components/common/heading/heading';
import { SchedulerForm } from 'components/shared/scheduler/scheduler';
import ErrorItemNotFound from 'components/app/errors/errorItemNotFound';
import DistributorFormControl from 'components/shared/distributor/distributorFormControl';
import BackofficeReportsService from 'services/backofficeReportsService';
import BackofficeReportsHelper from 'components/navigation/backofficeReports/backofficeReportsHelper';
import { Tabs, Tab } from 'components/common/tabs/tabs';
import AppContext from 'contexts/appContext';
import ReportSelection from 'components/navigation/backofficeReports/common/reportSelection';
import ReportPeriod from 'components/navigation/backofficeReports/common/reportPeriod';
import useForm from 'hooks/form';

export default function BackofficeReportsTaskEditPage(props) {
  const { setAppArea, setAppBreadcrumb } = useContext(AppContext);

  const { setFormRef, submitForm, renderFormValidationMessage } = useForm({ protect: true });

  const id = props.match.params.id;
  const [taskData, setTaskData] = useState();
  const [isPeriodRelative, setIsPeriodRelative] = useState(true);
  const [relativePeriodValue, setRelativePeriodSelectedValue] = useState('');
  const [relativeOffsetStartValue, setRelativeOffsetStartValue] = useState();
  const [relativeOffsetEndValue, setRelativeOffsetEndValue] = useState();
  const [absolutePeriodStartValue, setAbsolutePeriodStartSelectedValue] = useState();
  const [absolutePeriodEndValue, setAbsolutePeriodEndSelectedValue] = useState();
  const [selectedReportTypeId, setSelectedReportTypeId] = useState('');
  const [configurationId, setConfigurationId] = useState('');
  const [jobOptions, setJobOptions] = useState([]);
  const [scheduledData, setScheduledData] = useState(null);
  const [distributorData, setDistributorData] = useState(null);
  const [reportScheduleTab, setReportScheduleTab] = useState('demand');
  const [reportOutputTab, setReportOutputTab] = useState('preview');
  const [taskName, setTaskName] = useState('');
  const [recipientsList, setRecipientsList] = useState([]);
  const [types, setTypes] = useState([]);
  const [hierarchyId, setHierarchyId] = useState();
  const [dataNotFound, setDataNotFound] = useState(false);

  useAsyncEffect(async () => {
    setAppArea(AppContext.Areas.BackofficeReports, 'Task Setup');
    setAppBreadcrumb([{ name: 'Home', href: '/' }, { name: 'Backoffice Reports', href: '/BackofficeReports/Run' }, { name: 'Tasks', href: '/BackofficeReports/Tasks' }, { name: 'Task Details', href: `/BackofficeReports/Task/${id}` }, { name: 'Edit Task', selected: true }]);
    setTypes(await BackofficeReportsHelper.getReportTypes());
    const task = await BackofficeReportsService.API.getTask(id);
    if (task) {
      setTaskName(task.taskName);
      setSelectedReportTypeId(task.reportTypeId);
      setHierarchyId(task.hierarchyId);

      setIsPeriodRelative(task.isRelativeDate);
      setRelativePeriodSelectedValue(task.relativeDateValue);
      setRelativeOffsetStartValue(task.relativeStartOffset);
      setRelativeOffsetEndValue(task.relativeEndOffset);
      setAbsolutePeriodStartSelectedValue(task.absoluteStartDate);
      setAbsolutePeriodEndSelectedValue(task.absoluteEndDate);

      setReportScheduleTab(task.isScheduled ? 'schedule' : 'demand');
      setReportOutputTab(task.isDistributed ? 'distrubute' : 'preview');

      if (task.jobOptions) {
        setJobOptions(task.jobOptions);
      }

      if (task.scheduler) {
        setScheduledData(task.scheduler);
      }

      if (task.distributor) {
        setDistributorData(task.distributor);
      }
      else {
        setDistributorData({ subscribers: [] });
      }

      setTaskData(task);
      setConfigurationId(task.configurationId);
      setRecipientsList(await BackofficeReportsService.API.getRecipientsList());
    }
    else {
      setDataNotFound(true);
    }
  }, []);

  const handleSaveTaskClicked = submitForm(async (cancelSubmit) => {
    await BackofficeReportsService.API.updateTask(id, {
      taskName,
      configurationId: configurationId,
      isRelativeDate: isPeriodRelative,
      relativeDateValue: relativePeriodValue == null || relativePeriodValue === '' ? undefined : relativePeriodValue,
      relativeStartOffset: relativeOffsetStartValue == null || relativeOffsetStartValue === '' ? undefined : relativeOffsetStartValue,
      relativeEndOffset: relativeOffsetEndValue == null || relativeOffsetEndValue === '' ? undefined : relativeOffsetEndValue,
      absoluteStartDate: absolutePeriodStartValue == null || absolutePeriodStartValue === '' ? undefined : absolutePeriodStartValue,
      absoluteEndDate: absolutePeriodEndValue == null || absolutePeriodEndValue === '' ? undefined : absolutePeriodEndValue,
      isScheduled: reportScheduleTab === 'schedule',
      isDistributed: reportOutputTab === 'distrubute',
      scheduler: reportScheduleTab === 'schedule' ? scheduledData : undefined,
      distributor: reportOutputTab === 'distrubute' ? distributorData : undefined,
      jobOptions: jobOptions,
    });
    props.history.push(`/BackofficeReports/Task/${id}`);
  });

  const handleCancelClicked = () => {
    props.history.goBack();
  };

  const onChangeRelativePeriod = (newRelativePeriod) => {
    setRelativePeriodSelectedValue(newRelativePeriod);
    if (newRelativePeriod === 'daysoff') {
      setRelativeOffsetStartValue(1);
      setRelativeOffsetEndValue(null);
    }
    else if (newRelativePeriod === 'daysrangeoff') {
      setRelativeOffsetStartValue(1);
      setRelativeOffsetEndValue(2);
    }
    else if (newRelativePeriod === 'weeksoff') {
      setRelativeOffsetStartValue(1);
      setRelativeOffsetEndValue(null);
    }
    else {
      setRelativeOffsetStartValue(null);
      setRelativeOffsetEndValue(null);
    }
  };

  const onChangeAbsolutePeriodStartValue = (value) => {
    setAbsolutePeriodStartSelectedValue(value);
    setAbsolutePeriodEndSelectedValue(value);
  };

  const onChangeAbsolutePeriodEndValue = (value) => {
    setAbsolutePeriodEndSelectedValue(value);
  };

  if (dataNotFound) {
    return <ErrorItemNotFound />;
  }

  if (!taskData) {
    //Loading View
    return (
      <>
        <Heading />

        <div className='row justify-content-md-center mx-0 mt-4'>
          <div className='col-md-10 col-lg-8 col-xl-6'>
            <Panel>
              <Panel.SkeletonHeader width={200} />
              <Panel.SkeletonBody rows={7} rowHeight={54} rowMargin={22} />
              <Panel.SkeletonFooter width={250} />
            </Panel>
          </div>
        </div>
      </>
    );
  }

  return (
    <Flow>
      <Flow.Header>
        <Heading>
          <SiteContext.Button hierarchyId={hierarchyId} readOnly />
          <div className='flex-grow-1 d-flex justify-content-end'>
            <Inputs.Button variant='default' onClick={handleCancelClicked} iconClass='icons8-undo' label='Cancel' />
            <Inputs.Button variant='success' onClick={handleSaveTaskClicked} iconClass='icons8-save' label='Save' />
          </div>
        </Heading>
      </Flow.Header>

      <Flow.Body>
        <div className='row justify-content-md-center mx-0 mt-4'>
          <div className='col-md-10 col-lg-8 col-xl-6'>
            <Form ref={setFormRef} noValidate>
              <Panel>
                <Panel.Header>Edit Task</Panel.Header>
                <Panel.Body>
                  <Form.Group>
                    <Form.Label required>Description</Form.Label>
                    <Inputs.Text required placeholder='Please enter a description of the task' description='Description' value={taskName} onValueChange={setTaskName} />
                  </Form.Group>
                  <ReportSelection
                    hierarchyId={hierarchyId}
                    types={types}
                    selectedReportTypeId={selectedReportTypeId}
                    configurationId={configurationId}
                    onChangeConfigurationId={setConfigurationId}
                    jobOptions={jobOptions}
                    onChangeJobOptions={setJobOptions}
                    reportTypeReadOnly={true}
                  />
                  <ReportPeriod
                    types={types}
                    selectedReportTypeId={selectedReportTypeId}
                    relativePeriodValue={relativePeriodValue}
                    onChangeRelativePeriod={onChangeRelativePeriod}
                    relativeOffsetStartValue={relativeOffsetStartValue}
                    setRelativeOffsetStartValue={setRelativeOffsetStartValue}
                    relativeOffsetEndValue={relativeOffsetEndValue}
                    setRelativeOffsetEndValue={setRelativeOffsetEndValue}
                    absolutePeriodStartValue={absolutePeriodStartValue}
                    onChangeAbsolutePeriodStartValue={onChangeAbsolutePeriodStartValue}
                    absolutePeriodEndValue={absolutePeriodEndValue}
                    onChangeAbsolutePeriodEndValue={onChangeAbsolutePeriodEndValue}
                    isPeriodRelative={isPeriodRelative}
                    setIsPeriodRelative={setIsPeriodRelative}
                  />
                  <Form.Group>
                    <Form.Label>Schedule</Form.Label>
                    <Tabs defaultSelection='demand' selection={reportScheduleTab} onSelect={(key) => setReportScheduleTab(key)}>
                      <Tab value='demand' title='On Demand'>
                        <div>Report will only execute when ran manually.</div>
                      </Tab>
                      <Tab value='schedule' title='Scheduled'>
                        <SchedulerForm defaultValues={scheduledData} onChange={(newObj) => setScheduledData(newObj)} />
                      </Tab>
                    </Tabs>
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Output</Form.Label>
                    <Tabs defaultSelection='preview' selection={reportOutputTab} onSelect={(key) => setReportOutputTab(key)}>
                      <Tab value='preview' title='Preview'>
                        <div>Report output will only be displayed within the application.</div>
                      </Tab>
                      <Tab value='distrubute' title='Email'>
                        <Form.Group>
                          <Form.Label>Email Recipients</Form.Label>
                          <DistributorFormControl
                            defaultValues={distributorData}
                            recipientsList={recipientsList}
                            onCreateRecipient={async (firstName, lastName, emailAddress) => await BackofficeReportsService.API.insertRecipient(firstName, lastName, emailAddress)}
                            onChange={(selections, recipients) => {
                              setDistributorData({ subscribers: selections });
                              setRecipientsList(recipients);
                            }}
                          />
                        </Form.Group>
                      </Tab>
                    </Tabs>
                  </Form.Group>
                </Panel.Body>
                <Panel.Footer>
                  {renderFormValidationMessage()}
                  <Inputs.Button variant='default' onClick={handleCancelClicked} iconClass='icons8-undo' label='Cancel' />
                  <Inputs.Button variant='success' onClick={handleSaveTaskClicked} iconClass='icons8-save' label='Save' />
                </Panel.Footer>
              </Panel>
            </Form>
          </div>
        </div>
      </Flow.Body>
    </Flow>
  );
}
